import axios, { AxiosRequestConfig } from "axios";
import { saveAs } from "file-saver";
import { Audit } from "../models/Audit";
import { AuditDetail } from "../models/AuditDetail";
import { AuditModule } from "../models/AuditModule";
import { AuditQuestion } from "../models/AuditQuestion";
import { Section } from "../models/Section";

const API_BASE_URL = process.env.REACT_APP_AUDIT_API_URL;

// ============================================
// TIPOS PARA PAGINACIÓN
// ============================================

export interface PagedResult<T> {
  data: T[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// ============================================
// MÉTODOS EXISTENTES - SIN CAMBIOS
// ============================================

const getQuestionaryById = async (id: number): Promise<Audit> => {
  const response = await fetch(`${API_BASE_URL}/Audit/GetById?id=${id}`);
  const data = await response.json();
  return data;
};

const getSections = async (policyTypeId: number): Promise<Section[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/GetSections?policyTypeId=${policyTypeId}`,
  );
  const data = await response.json();
  return data;
};

const getModules = async (policyTypeId: number): Promise<AuditModule[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/GetAuditModules?policyTypeId=${policyTypeId}`,
  );
  const data = await response.json();
  return data;
};

const getQuestions = async (
  subsectionId: number,
  questionaryId: number,
): Promise<AuditQuestion[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/GetQuestions?subsectionId=${subsectionId}&auditId=${questionaryId}`,
  );
  const data = await response.json();
  return data;
};

const getQuestionById = async (questionId: number): Promise<AuditQuestion> => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/GetQuestionById?questionId=${questionId}`,
  );
  const data = await response.json();
  return data;
};

const getQuestionaryDetail = async (
  questionaryId: number,
): Promise<AuditDetail[]> => {
  const response = await fetch(
    `${API_BASE_URL}/AuditDetail/GetByQuestionaryId?questionaryId=${questionaryId}`,
  );
  const data = await response.json();
  return data;
};

const saveQuestionaryDetail = async (questionaryDetails: AuditDetail[]) => {
  const response = await fetch(`${API_BASE_URL}/AuditDetail/Save`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(questionaryDetails),
  });

  return response;
};

const saveQuestionary = async (questionary: Audit) => {
  const response = await fetch(`${API_BASE_URL}/Audit/Save`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(questionary),
  });

  return response;
};

const changeStatus = async (questionaryId: number, statusId: number) => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/ChangeStatus?auditId=${questionaryId}&statusId=${statusId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json; charset= utf-8" },
    },
  );

  return response;
};

const saveComments = async (questionaryDetails: AuditDetail[]) => {
  const response = await fetch(`${API_BASE_URL}/AuditDetail/SaveComments`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(questionaryDetails),
  });

  return response;
};

const getQuestionaryDetailIsSaved = async (
  questionaryId: number,
): Promise<Boolean[]> => {
  const response = await fetch(
    `${API_BASE_URL}/AuditDetail/IsSaved?questionaryId=${questionaryId}`,
  );
  const data = await response.json();
  return data;
};

const exportToExcel = async (questionaryId: number) => {
  let url = `${API_BASE_URL}/AuditDetail/ExportToExcel?questionaryId=${questionaryId}`;
  let method = "GET";
  let instance = axios.create({ baseURL: url });
  let options: AxiosRequestConfig = {
    url,
    method,
    responseType: "blob",
  };
  return instance.request<any>(options).then((response) => {
    let url = window.URL.createObjectURL(new Blob([response.data]));
    saveAs(url, "Cuestionario.xlsx");
  });
};

const deleteAudit = async (auditId: number) => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/Delete?auditId=${auditId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json; charset= utf-8" },
    },
  );

  return response;
};

const getAllQuestionsForType = async (
  auditTypeId: number,
): Promise<AuditQuestion[]> => {
  const response = await fetch(
    `${API_BASE_URL}/Audit/GetAllQuestionsForType?auditTypeId=${auditTypeId}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch all questions for type");
  }
  const data = await response.json();
  return data;
};

const getAuditDetailById = async (
  auditDetailId: number,
): Promise<AuditDetail> => {
  const response = await fetch(
    `${API_BASE_URL}/AuditDetail/GetDetailById?auditDetailId=${auditDetailId}`,
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch questionary detail by ID: ${errorText}`);
  }
  const data = await response.json();
  return data;
};

const archived = async (audit: Audit) => {
  const response = await fetch(`${API_BASE_URL}/Audit/Archive`, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset= utf-8" },
    body: JSON.stringify(audit),
  });

  return response;
};

// ============================================
// MÉTODOS OPTIMIZADOS CON PAGINACIÓN
// ============================================

/**
 * OPTIMIZADO: Obtiene auditorías por usuario con soporte de paginación
 * Si no se pasan pageNumber/pageSize, se comporta como antes (trae todas)
 */
const getQuestionariesByUserId = async (
  userId: number,
  isCompleted: boolean,
  pageNumber?: number,
  pageSize?: number,
  includeArchived?: boolean,
  searchText?: string,
): Promise<Audit[] | PagedResult<Audit>> => {
  const params = new URLSearchParams({
    userId: userId.toString(),
    isCompleted: isCompleted ? "true" : "false",
  });

  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (includeArchived !== undefined)
    params.append("includeArchived", includeArchived.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Audit/GetQuestionariesByUserId?${params.toString()}`,
  );
  const data = await response.json();

  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Audit>;
  }

  return data as Audit[];
};

/**
 * OPTIMIZADO: Obtiene todas las auditorías con soporte de paginación
 * Si no se pasan pageNumber/pageSize, se comporta como antes (trae todas)
 */
const getAllQuestionaries = async (
  isCompleted: boolean,
  pageNumber?: number,
  pageSize?: number,
  includeArchived?: boolean,
  searchText?: string,
): Promise<Audit[] | PagedResult<Audit>> => {
  const params = new URLSearchParams({
    isCompleted: isCompleted ? "true" : "false",
  });

  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (includeArchived !== undefined)
    params.append("includeArchived", includeArchived.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Audit/GetAllQuestionaries?${params.toString()}`,
  );
  const data = await response.json();

  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Audit>;
  }

  return data as Audit[];
};

/**
 * NUEVO: Obtiene solo auditorías archivadas con soporte de paginación
 */
const getArchivedAudits = async (
  userId?: number,
  role?: string,
  pageNumber?: number,
  pageSize?: number,
  searchText?: string,
): Promise<Audit[] | PagedResult<Audit>> => {
  const params = new URLSearchParams();

  if (userId) params.append("userId", userId.toString());
  if (role) params.append("role", role);
  if (pageNumber !== undefined)
    params.append("pageNumber", pageNumber.toString());
  if (pageSize !== undefined) params.append("pageSize", pageSize.toString());
  if (searchText) params.append("searchText", searchText);

  const response = await fetch(
    `${API_BASE_URL}/Audit/GetArchivedAudits?${params.toString()}`,
  );

  if (!response.ok) {
    throw new Error("Error al cargar auditorías archivadas");
  }

  const data = await response.json();

  if (data.data && data.totalRecords !== undefined) {
    return data as PagedResult<Audit>;
  }

  return data as Audit[];
};

// ============================================
// EXPORTAR SERVICIO
// ============================================

const AuditService = {
  // Métodos existentes (sin cambios)
  getQuestionaryById,
  getSections,
  getQuestions,
  getQuestionById,
  saveQuestionaryDetail,
  getQuestionaryDetail,
  getQuestionaryDetailIsSaved,
  saveComments,
  exportToExcel,
  saveQuestionary,
  getModules,
  changeStatus,
  deleteAudit,
  getAllQuestionsForType,
  getAuditDetailById,
  archived,

  // Métodos optimizados (backward compatible)
  getQuestionariesByUserId,
  getAllQuestionaries,

  // Nuevo método
  getArchivedAudits,
};

export default AuditService;
